import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTheme, alpha } from '@mui/material/styles';
import HeroBackground from './HeroBackground';
import { textGradient } from '../../../../theme/mixins';
import { varAlpha } from '../../../../theme/palette';

const Hero = () => {
  const theme = useTheme();
  const heroRef = useRef(null);

  // Scroll-based parallax effect (content fades and moves up slightly when scrolling)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  // Transform scroll progress to opacity (fades out as you scroll)
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Transform scroll progress to y position (moves up as you scroll)
  const y = useTransform(scrollYProgress, [0, 0.5], [0, -50]);

  return (
    <Box
      ref={heroRef}
      sx={{
        position: 'relative',
        minHeight: { xs: '80vh', md: '100vh' },
        maxHeight: { md: 1440 },
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* ANIMATED PCB CIRCUIT BACKGROUND */}
      {/* To disable this background, simply comment out the line below */}
      <HeroBackground />

      {/* CONTENT - With scroll parallax effect */}
      <Container
        component={motion.div}
        style={{ opacity, y }}
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          pt: { xs: 12, md: 18 },
        }}
      >
        {/* HEADLINE */}
        <Typography
          component="h1"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            ...theme.typography.h2,
            my: 0,
            mx: 'auto',
            maxWidth: 680,
            fontFamily: theme.typography.fontSecondaryFamily,
            [theme.breakpoints.up('lg')]: { fontSize: 72, lineHeight: '100px' },
          }}
        >
          <Box component="span" sx={{ width: 1, color: 'text.primary', fontWeight: 800 }}>
            A Workspace for
          </Box>
          <Box
            component={motion.span}
            animate={{ backgroundPosition: '200% center' }}
            transition={{
              duration: 20,
              ease: 'linear',
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            sx={{
              ...textGradient(`300deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 50%, ${theme.palette.primary.main} 100%`),
              backgroundSize: '400%',
              display: 'inline-block',
              ml: { xs: 0.75, md: 1, xl: 1.5 },
              whiteSpace: 'nowrap',
            }}
          >
            Hardware Development
          </Box>
        </Typography>

        {/* SUBHEADLINE */}
        <Typography
          variant="body2"
          sx={{
            mx: 'auto',
            [theme.breakpoints.up('sm')]: { whiteSpace: 'pre-line' },
            [theme.breakpoints.up('lg')]: { fontSize: 20, lineHeight: '40px' },
            color: theme.palette.text.primary,
            // maxWidth: 920,
            mb: 6,
            lineHeight: 2,
            textAlign: 'center',
          }}
        >
          {`We're building a context layer between engineers and design tools for hardware engineering companies`}
        </Typography>

        {/* CTA BUTTONS */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* <Button
            variant="contained"
            color="inherit"
            size="large"
            href="/trialsignup"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
            }}
          >
            Get Started
          </Button> */}

          <Button
            variant="outlined"
            color="inherit"
            size="large"
            href="/contactus"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              borderColor: 'text.primary',
            }}
          >
            Schedule Demo
          </Button>
        </Box>

        {/* Social proof removed per request */}

        {/* INLINE CUSTOMER LOGOS - Subtle & Minimal */}
        {/* 
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            pt: { xs: 4, md: 5 },
            borderTop: '1px solid',
            borderColor: alpha('#919EAB', 0.12),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#919EAB',
              letterSpacing: '1.2px',
              mb: 3,
              display: 'block',
            }}
          >
            Accelerating with us
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
              flexWrap: 'wrap',
            }}
          >
            <Box
              component={motion.div}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              sx={{
                width: { xs: 120, md: 140 },
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#00A76F', 0.04),
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha('#00A76F', 0.12),
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha('#00A76F', 0.08),
                  borderColor: alpha('#00A76F', 0.24),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#00A76F',
                }}
              >
                Customer 1
              </Typography>
            </Box>

            <Box
              component={motion.div}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              sx={{
                width: { xs: 120, md: 140 },
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#00A76F', 0.04),
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha('#00A76F', 0.12),
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha('#00A76F', 0.08),
                  borderColor: alpha('#00A76F', 0.24),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#00A76F',
                }}
              >
                Customer 2
              </Typography>
            </Box>
          </Box>
        </Box>
        */}
      </Container>
    </Box>
  );
};

export default Hero;
